import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useMotion, EASE_SNAP, EASE_POP } from './motion';

interface PressableProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  children: ReactNode;
  /** Optional: render as different element role (default: button) */
  as?: 'button';
}

/**
 * Pressable — wraps any element with press-down feedback.
 * Quick scale-in on pointerdown, springy return on release.
 *
 * ALWAYS renders a real <button>. Keyboard-accessible by default.
 * The JSX artifact used <div> — this is the corrected version.
 */
export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(
  ({ children, className, style, disabled, ...rest }, ref) => {
    const [pressed, setPressed] = useState(false);
    const { reduced } = useMotion();

    const pressScale = reduced ? 1 : 0.95;
    const pressDur = reduced ? 0 : 90;
    const releaseDur = reduced ? 0 : 260;

    return (
      <button
        ref={ref}
        className={className}
        disabled={disabled}
        onPointerDown={(e) => {
          setPressed(true);
          rest.onPointerDown?.(e);
        }}
        onPointerUp={(e) => {
          setPressed(false);
          rest.onPointerUp?.(e);
        }}
        onPointerLeave={(e) => {
          setPressed(false);
          rest.onPointerLeave?.(e);
        }}
        onPointerCancel={(e) => {
          setPressed(false);
          rest.onPointerCancel?.(e);
        }}
        style={{
          // Reset button defaults FIRST so callers can style freely.
          // (Previously these resets came AFTER ...style, silently clobbering
          // every caller's background/border/padding/color/font — SegmentedControl,
          // ChipToggle, SaveBar, BottomNav, DurationPicker all passed styles that
          // were discarded.)
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          font: 'inherit',
          color: 'inherit',
          ...style,
          // Motion-critical values always win over caller styles
          transform: pressed ? `scale(${pressScale})` : 'scale(1)',
          transition: `transform ${pressed ? pressDur : releaseDur}ms ${pressed ? EASE_SNAP : EASE_POP}`,
          cursor: disabled ? 'default' : 'pointer',
          touchAction: 'manipulation',
        }}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

Pressable.displayName = 'Pressable';
